import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Star, CheckCircle2, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function PublicSurvey() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/survey/${token}`);
                setData(data);
                if (data.survey?.status === "submitted") {
                    setResult({ already: true });
                }
            } catch (e) {
                setError(formatApiErrorDetail(e.response?.data?.detail) || "Survey not found");
            } finally { setLoading(false); }
        })();
    }, [token]);

    const submit = async () => {
        setSubmitting(true);
        try {
            const { data: out } = await api.post(`/survey/${token}/submit`, { answers });
            setResult(out);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally { setSubmitting(false); }
    };

    if (loading) return <Wrap><Loader2 className="animate-spin mx-auto" /></Wrap>;
    if (error)   return <Wrap><div className="text-center text-red-700">{error}</div></Wrap>;
    if (!data)   return null;

    if (result) {
        const rating = result.overall_rating ?? null;
        const showReview = !!data.template?.google_review_url && (rating ?? 0) >= 4;
        return (
            <Wrap>
                <div className="text-center py-6">
                    <CheckCircle2 size={48} className="mx-auto text-[#ff7b02]" />
                    <h1 className="mt-4 text-xl font-semibold tracking-tight">Thank you!</h1>
                    <p className="mt-1 text-[13px] text-muted-foreground">
                        Your feedback helps us improve. {result.already && " (Already submitted)"}
                    </p>
                    {showReview && (
                        <div className="mt-6">
                            <p className="text-[13px] mb-2">
                                We're thrilled you enjoyed your stay! Would you share that on Google?
                            </p>
                            <a href={data.template.google_review_url} target="_blank" rel="noreferrer"
                               className="inline-flex items-center gap-1 rounded-md bg-[#ff7b02] px-4 py-2 text-sm font-medium text-white hover:bg-[#ff7b02]/90"
                               data-testid="ps-google-cta">
                                <ExternalLink size={14} /> Leave a Google review
                            </a>
                        </div>
                    )}
                </div>
            </Wrap>
        );
    }

    return (
        <Wrap>
            <div className="mb-5">
                <h1 className="text-xl font-semibold tracking-tight" data-testid="ps-title">{data.template.subject || "How was your stay?"}</h1>
                <p className="mt-1 text-[13px] text-muted-foreground">{data.template.intro}</p>
                {data.reservation?.check_in && (
                    <p className="mt-1 text-[11px] text-muted-foreground">
                        Stay: {data.reservation.check_in} → {data.reservation.check_out}
                    </p>
                )}
            </div>

            <div className="space-y-5" data-testid="ps-questions">
                {(data.template.questions || []).map((q) => (
                    <Question key={q.id} q={q}
                               value={answers[q.id]}
                               onChange={(v) => setAnswers((s) => ({ ...s, [q.id]: v }))} />
                ))}
            </div>

            <button onClick={submit} disabled={submitting}
                    className="mt-6 w-full rounded-md bg-[#ff7b02] px-4 py-3 text-sm font-medium text-white disabled:opacity-60 hover:bg-[#ff7b02]/90"
                    data-testid="ps-submit">
                {submitting ? "Submitting…" : "Submit feedback"}
            </button>
        </Wrap>
    );
}

function Question({ q, value, onChange }) {
    return (
        <div className="rounded-md border border-slate-200 bg-white p-4">
            <div className="text-[13px] font-medium">{q.label}{q.required && <span className="text-red-600"> *</span>}</div>
            <div className="mt-2">
                {q.type === "rating" && (
                    <StarBar value={Number(value || 0)} onChange={onChange} testid={`ps-q-${q.id}`} />
                )}
                {q.type === "nps" && (
                    <NpsBar value={value === undefined || value === "" ? null : Number(value)} onChange={onChange} testid={`ps-q-${q.id}`} />
                )}
                {q.type === "text" && (
                    <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3}
                              placeholder="Tell us more…"
                              className="w-full rounded-md border border-slate-300 px-3 py-2 text-[13px] focus:border-[#ff7b02] focus:outline-none focus:ring-1 focus:ring-[#ff7b02]"
                              data-testid={`ps-q-${q.id}`} />
                )}
                {q.type === "select" && (
                    <select value={value || ""} onChange={(e) => onChange(e.target.value)}
                            className="w-full rounded-md border border-slate-300 px-3 py-2 text-[13px] focus:border-[#ff7b02] focus:outline-none focus:ring-1 focus:ring-[#ff7b02]"
                            data-testid={`ps-q-${q.id}`}>
                        {(q.options || []).map((o) => (
                            <option key={o} value={o}>{o || "None"}</option>
                        ))}
                    </select>
                )}
            </div>
        </div>
    );
}

function StarBar({ value, onChange, testid }) {
    return (
        <div className="flex gap-1" data-testid={testid}>
            {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => onChange(n)} type="button"
                        data-testid={`${testid}-${n}`}
                        className="p-1 hover:scale-110 transition-transform">
                    <Star size={26} className={n <= value ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
                </button>
            ))}
        </div>
    );
}

function NpsBar({ value, onChange, testid }) {
    return (
        <div className="flex flex-wrap gap-1" data-testid={testid}>
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                <button key={n} onClick={() => onChange(n)} type="button"
                        data-testid={`${testid}-${n}`}
                        className={`h-9 w-9 rounded-md border text-[12.5px] font-medium transition ${
                            value === n
                                ? "border-[#ff7b02] bg-[#ff7b02] text-white"
                                : "border-slate-300 text-slate-700 hover:border-[#ff7b02]"
                        }`}>
                    {n}
                </button>
            ))}
        </div>
    );
}

function Wrap({ children }) {
    return (
        <div className="min-h-screen bg-slate-50" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
            <div className="mx-auto max-w-2xl px-4 py-8">
                <div className="mb-5 flex items-center gap-2">
                    <div className="h-7 w-7 rounded-md bg-[#ff7b02] text-white flex items-center justify-center text-[12px] font-semibold">A</div>
                    <span className="text-[14px] font-semibold tracking-tight">Akomoda</span>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                    {children}
                </div>
                <div className="mt-3 text-center text-[11px] text-slate-400">
                    Powered by Akomoda
                </div>
            </div>
        </div>
    );
}
